import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import distributorService from '../services/distributorService';
import { DistributorActivity } from '../components/distributor/DistributorActivity';
import AdminDistributorCustomerList from '../components/distributor/AdminDistributorCustomerList';
import useShippingRequest from '../hooks/useShippingRequest';
import { useT } from '../i18n/LanguageContext';

const titles = {
  customers: 'Distributor Customers',
  orders: 'Distributor Orders',
  ivrs: 'Distributor IVRs',
  shipments: 'Distributor Shipments',
};
export default function AdminDistributorRecords({ kind }) {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const ids = params.getAll('distributorId');
  const id = ids.length === 1 && /^[a-f\d]{24}$/.test(ids[0]) ? ids[0] : '';
  const invalid = ids.length > 0 && !id;
  const { begin, version } = useShippingRequest('admin-distributor-record-options');
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const rows = options?.version === version ? options.rows : null;
  const load = useCallback(async () => {
    const request = begin();
    setOptions(null);
    setError('');
    try {
      const data = (await distributorService.adminDistributors())?.data?.data;
      if (
        !Array.isArray(data) ||
        data.some((row) => !/^[a-f\d]{24}$/.test(row._id) || typeof row.name !== 'string')
      )
        throw new Error('Invalid options');
      if (request.isCurrent()) setOptions({ rows: data, version });
    } catch {
      if (request.isCurrent()) setError('Could not load distributors. Please try again.');
    }
  }, [begin, version]);
  useEffect(() => {
    load();
  }, [load]);
  const unavailable = invalid || (id && rows && !rows.some((row) => row._id === id));
  const filter = (
    <select
      aria-label={t('Distributor')}
      value={id}
      disabled={!rows}
      className="h-[46px] max-w-full px-3 border border-[#e2e8f0] rounded-[10px] shadow-sm text-sm text-[#0f172a] bg-white"
      onChange={(event) => {
        const next = new URLSearchParams(params);
        next.delete('distributorId');
        if (event.target.value) next.set('distributorId', event.target.value);
        setParams(next);
      }}
    >
      <option value="">{t('All distributors')}</option>
      {id && !rows?.some((row) => row._id === id) && (
        <option value={id}>{t('Selected distributor')}</option>
      )}
      {rows?.map((row) => (
        <option key={row._id} value={row._id}>
          {row.name}
          {row.isActive === false ? ` (${t('Inactive')})` : ''}
        </option>
      ))}
    </select>
  );
  return (
    <div>
      {error && (
        <p role="alert" className="mb-4 text-sm text-red-700">
          {t(error)}{' '}
          <button className="underline" onClick={load}>
            {t('Retry')}
          </button>
        </p>
      )}
      {unavailable ? (
        <>
          <h1 className="text-[28px] font-bold text-[#0f172a] mb-6">{t(titles[kind])}</h1>
          {filter}
          <p role="alert" className="my-4 text-red-700">
            {t('This distributor is unavailable. Choose a distributor from the list.')}
          </p>
        </>
      ) : // A selected URL waits for validated options; the default overview never requires a selection.
      id && !rows ? (
        <p role="status">{t('Loading distributors...')}</p>
      ) : kind === 'customers' ? (
        <AdminDistributorCustomerList
          key={`${version}:${id}`}
          distributorId={id}
          distributors={rows || []}
          filter={filter}
        />
      ) : (
        <DistributorActivity
          key={`${version}:${id}:${kind}`}
          t={t}
          activityKind={kind}
          adminOverview
          adminDistributorId={id}
          distributors={rows || []}
          pageHeader={{
            title: titles[kind],
            subtitle:
              kind === 'orders'
                ? 'Manage orders and track shipments for your practices.'
                : kind === 'ivrs'
                  ? 'Review, verify and approve order requests from IVR details.'
                  : 'Shipment statuses match their orders and update automatically.',
            filterAction: filter,
          }}
        />
      )}
    </div>
  );
}
